import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Tab,
  Tabs,
  Paper,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  EditOutlined,
  EmailOutlined,
  PhoneOutlined,
  WorkspacePremiumOutlined,
  DeleteForeverOutlined,
  MarkEmailUnread,
  ManageAccountsOutlined,
  MoreVertOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { TextField, Snackbar, Tooltip } from '@mui/material';

import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { useTenantStore } from '../../stores/tenant.store';
import { db } from '../../lib/firebase';
import { portalViewApi } from '../../lib/api/portal-view.api';
import { usePortalViewStore } from '../../stores/portal-view.store';
import { PortalAccessCard } from './components/PortalAccessCard';
import { usePermissions } from '../../hooks/use-permissions';
import { StudentTranscript } from './components/StudentTranscript';
import { ProgressByProgramTab } from './components/ProgressByProgramTab';
import { ProgramEnrollmentTab } from './components/ProgramEnrollmentTab';
import { CrmTimeline } from './components/CrmTimeline';
import { PersonAdmissionsTab } from './components/PersonAdmissionsTab';
import { PersonEnrollmentsTab } from './components/PersonEnrollmentsTab';
import { TuitionSummaryCard } from './components/TuitionSummaryCard';
import { StudentScholarshipsTab } from './components/StudentScholarshipsTab';
import { EditPersonDialog } from './components/PersonDialogs';
import { formatDate } from '../../lib/i18n-format';
import { callable } from '../../lib/callable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DECEASED_IN_SOURCE_TAG, isDeceasedInSource, isModuleEnabled } from '@dosfilos/domain';
import { useTenantConfig } from '../../hooks/use-tenant-config';
import { credentialProgressApi } from '../../lib/api/credential-progress.api';
import { errorText } from '../../lib/error-text';
import { personDetailTabs, type PersonDetailTabKey } from './person-detail.tabs';
import {
  canGiveAccess,
  canOpenPortalView,
  firstTermName,
  hasStudentPortal,
  registrationOf,
} from './person-sheet.model';
import { headerEmail } from './person-header';
import { AcademicRecordTab } from './components/academic-record/AcademicRecordTab';
import { CredentialProgressTab } from './components/academic-record/CredentialProgressTab';

interface PersonDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  types: string[];
  institutionalRoles: string[];
  tags: string[];
  organizationName?: string;
  notes?: string;
  /** Null si la persona no tiene fecha de alta (la ficha dice «—»). */
  createdAt: string | null;
  authUid?: string;
  /** El código de alumno (L-######), si lo tiene. */
  studentCode?: string;
  /** Si vino de una importación: cuándo. Ausente = creada en el sistema. */
  origin?: { importedAt: string };
}

interface TabPanelProps {
  children?: React.ReactNode;
  /** La clave de la pestaña: los paneles se eligen por clave, no por índice. */
  index: PersonDetailTabKey;
  value: PersonDetailTabKey;
}

const TYPE_COLORS: Record<
  string,
  'primary' | 'success' | 'warning' | 'info' | 'default' | 'error'
> = {
  // Lifecycle
  student: 'primary',
  alumni: 'info',
  'prospecto-académico': 'warning',
  externo: 'error',
  contact: 'default',
  // Institutional
  faculty: 'success',
  docente: 'success',
  tutor: 'success',
  decano: 'success',
  academico: 'success',
  coordinador: 'success',
  capellan: 'success',
  staff: 'warning',
  // System (TenantRole)
  tenantAdmin: 'error',
  registrar: 'warning',
  reviewer: 'info',
  mentor: 'success',
  finance: 'success',
  donor: 'default',
};

const LIFECYCLE_TYPES = ['student', 'alumni', 'prospecto-académico', 'externo', 'contact'];
const INSTITUTIONAL_TYPES = [
  'faculty',
  'docente',
  'tutor',
  'decano',
  'academico',
  'coordinador',
  'capellan',
  'staff',
];
const SYSTEM_ROLES = ['tenantAdmin', 'registrar', 'reviewer', 'mentor', 'finance', 'donor'];

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`person-tabpanel-${index}`}
      aria-labelledby={`person-tab-${index}`}
      {...other}
      style={{ height: '100%', overflow: 'auto' }}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export function PersonDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['sis', 'common']);
  const { currentTenantId } = useTenantStore();
  const { isAdmin, hasAnyRole } = usePermissions();
  // «Ver portal» (lib/portal-view): tenantAdmin, registro y la plataforma.
  const canViewPortal = hasAnyRole(['tenantAdmin', 'registrar', 'platformAdmin']);
  const queryClient = useQueryClient();
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  const [tabValue, setTabValue] = useState<PersonDetailTabKey>('general');
  const [loading, setLoading] = useState(false);
  const [person, setPerson] = useState<PersonDetail | null>(null);

  // `graduation_audit`: las pestañas 3 y 4 muestran el expediente y el avance
  // de la credencial en lugar de las notas numéricas. Una sola lectura las
  // alimenta a las dos, y solo cuando alguna se abre.
  const { academicRecord, modules } = useTenantConfig();
  // M4·D1: configuración con nombre (la bandera de respaldo mientras conviven).
  const credentialRecord = academicRecord.mode === 'credentials';
  const importedRecord = academicRecord.imported;
  const progressQuery = useQuery({
    queryKey: ['credential-progress', currentTenantId, id],
    enabled:
      credentialRecord &&
      !!currentTenantId &&
      !!id &&
      (tabValue === 'academicRecord' || tabValue === 'credentialProgress'),
    queryFn: () => credentialProgressApi.get({ tenantId: currentTenantId!, personId: id! }),
  });
  const isPlatformAdmin = hasAnyRole(['platformAdmin']);
  // «Primer período» de una persona importada: el más antiguo en que tiene una
  // inscripción, calculado de sus inscripciones (no un dato guardado).
  const firstTermQ = useQuery({
    queryKey: ['person-first-term', currentTenantId, id],
    enabled: !!currentTenantId && !!id,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const enr = await getDocs(
        query(
          collection(db, `tenants/${currentTenantId}/enrollments`),
          where('studentId', '==', id),
        ),
      );
      const termIds = [...new Set(enr.docs.map((d) => d.data()['termId'] as string))];
      const terms = new Map<string, { name: string; startDate: string }>();
      await Promise.all(
        termIds.map(async (termId) => {
          const t = await getDoc(doc(db, `tenants/${currentTenantId}/terms`, termId));
          const data = t.data();
          if (data) {
            terms.set(termId, {
              name: (data['name'] as string) ?? termId,
              // `YYYY-MM-DD` (o un Timestamp en datos viejos): ordena igual.
              startDate:
                typeof data['startDate'] === 'string'
                  ? data['startDate']
                  : (data['startDate']?.toDate?.()?.toISOString?.() ?? ''),
            });
          }
        }),
      );
      return firstTermName(
        enr.docs.map((d) => ({ termId: d.data()['termId'] as string })),
        terms,
      );
    },
  });
  const [isEditing, setIsEditing] = useState(false);

  // Nuclear delete confirm dialog
  const [nuclearOpen, setNuclearOpen] = useState(false);
  const [nuclearConfirmName, setNuclearConfirmName] = useState('');
  const [nuclearDeleting, setNuclearDeleting] = useState(false);
  const [nuclearError, setNuclearError] = useState<string | null>(null);

  // Resend welcome email
  const [resending, setResending] = useState(false);
  const [resendSnack, setResendSnack] = useState<{
    open: boolean;
    msg: string;
    severity: 'success' | 'error';
  }>({ open: false, msg: '', severity: 'success' });

  // Update student email dialog
  const [updateEmailOpen, setUpdateEmailOpen] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [updateEmailError, setUpdateEmailError] = useState<string | null>(null);

  // Actions dropdown menu
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const actionsMenuOpen = Boolean(actionsMenuAnchor);

  // ── Fetch real Person from Firestore ───────────────────────────────────
  useEffect(() => {
    if (!currentTenantId || !id) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const personRef = doc(db, `tenants/${currentTenantId}/people`, id);
    const unsub = onSnapshot(
      personRef,
      (snap) => {
        if (!snap.exists()) {
          setPerson(null);
        } else {
          const data = snap.data();
          setPerson({
            id: snap.id,
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: data.email || '',
            phone: data.phone,
            types: data.types || [],
            institutionalRoles: data.institutionalRoles || [],
            tags: data.tags || [],
            organizationName: data.organizationName,
            notes: data.notes,
            authUid: data.authUid,
            // Sin fecha de alta, null: la ficha dice «—», nunca la de hoy.
            createdAt: data.createdAt?.toDate?.()?.toISOString() ?? null,
            ...(typeof data.studentCode === 'string' ? { studentCode: data.studentCode } : {}),
            ...(data.origin?.kind === 'import' && data.origin?.importedAt?.toDate
              ? { origin: { importedAt: data.origin.importedAt.toDate().toISOString() } }
              : {}),
          });
        }
        setLoading(false);
      },
      () => setLoading(false),
    );

    return unsub;
  }, [currentTenantId, id]);

  const handleNuclearDelete = async () => {
    if (!currentTenantId || !id || !person) return;
    const fullName = `${person.firstName} ${person.lastName}`.trim().toUpperCase();
    if (nuclearConfirmName.trim().toUpperCase() !== fullName) return;

    setNuclearDeleting(true);
    setNuclearError(null);
    try {
      const hardDeleteFn = callable('hardDeletePerson');
      await hardDeleteFn({ tenantId: currentTenantId, personId: id });
      setNuclearOpen(false);
      navigate('/people', { replace: true });
    } catch (err: any) {
      setNuclearError(err.message || 'Error al eliminar permanentemente');
    } finally {
      setNuclearDeleting(false);
    }
  };

  const handleUpdateEmail = async () => {
    if (!currentTenantId || !id || !newEmailInput.trim()) return;
    setUpdatingEmail(true);
    setUpdateEmailError(null);
    try {
      const updateFn = callable('updateStudentEmail');
      await updateFn({ tenantId: currentTenantId, personId: id, newEmail: newEmailInput.trim() });
      setUpdateEmailOpen(false);
      setNewEmailInput('');
      setResendSnack({
        open: true,
        msg: '✅ Correo de acceso actualizado correctamente',
        severity: 'success',
      });
    } catch (err: any) {
      setUpdateEmailError(err.message || 'Error al actualizar el correo');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleResendWelcomeEmail = async () => {
    if (!currentTenantId || !id) return;
    setResending(true);
    try {
      const resendFn = callable('resendStudentWelcomeEmail');
      await resendFn({ tenantId: currentTenantId, personId: id });
      setResendSnack({
        open: true,
        msg: '✅ Email de bienvenida reenviado correctamente',
        severity: 'success',
      });
    } catch (err: any) {
      setResendSnack({
        open: true,
        msg: err.message || 'Error al reenviar el email',
        severity: 'error',
      });
    } finally {
      setResending(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!person) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" color="error">
          {t('people.profile.notFound')}
        </Typography>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/people')} sx={{ mt: 2 }}>
          {t('people.profile.back')}
        </Button>
      </Container>
    );
  }

  const isActiveStudent = person.types.includes('student');
  const isAlumni = person.types.includes('alumni');
  // El sistema de origen la registra como fallecida: se avisa, y no se ofrece
  // ningún acceso (el servidor lo rechaza igual: `person.deceased_in_source`).
  const deceased = isDeceasedInSource(person.tags);
  const tabs = personDetailTabs({
    credentialRecord,
    isActiveStudent,
    // Las pestañas de un módulo que el seminario no usa no aparecen.
    moduleEnabled: (key) => isModuleEnabled(modules, key),
  });
  // Si la pestaña elegida desapareció (se apagó su módulo), vuelve a General.
  const activeTab = tabs.some((tab) => tab.key === tabValue) ? tabValue : 'general';
  const registration = registrationOf(person);
  const progressPanel = (render: (p: NonNullable<typeof progressQuery.data>) => JSX.Element) =>
    progressQuery.isError ? (
      <Alert severity="error">{errorText(progressQuery.error)}</Alert>
    ) : !progressQuery.data ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    ) : (
      render(progressQuery.data)
    );

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header Profile Card */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundImage: 'none',
          bgcolor: 'background.paper',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '100px',
            background: isAlumni
              ? 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)'
              : isActiveStudent
                ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                : 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
            opacity: 0.12,
          }}
        />

        <Stack
          direction="row"
          spacing={{ xs: 2, sm: 3 }}
          alignItems="flex-start"
          sx={{ position: 'relative', zIndex: 1, mt: 2 }}
        >
          <IconButton
            onClick={() => navigate('/people')}
            sx={{
              position: 'absolute',
              top: -16,
              left: -16,
              bgcolor: 'background.paper',
              boxShadow: 1,
            }}
            size="small"
          >
            <ArrowBack fontSize="small" />
          </IconButton>

          <Avatar
            sx={{
              width: { xs: 72, sm: 100 },
              height: { xs: 72, sm: 100 },
              bgcolor: isAlumni ? 'info.main' : 'primary.main',
              fontSize: { xs: '1.75rem', sm: '2.5rem' },
              fontWeight: 600,
              boxShadow: 3,
              border: '4px solid',
              borderColor: 'background.paper',
              flexShrink: 0,
            }}
          >
            {person.firstName[0]}
            {person.lastName[0]}
          </Avatar>

          <Box sx={{ flexGrow: 1 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1.5}
            >
              <Box>
                <Typography variant="h4" fontWeight="700" gutterBottom>
                  {person.firstName} {person.lastName}
                </Typography>
                {person.studentCode && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: -0.5, mb: 1, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {t('sis:people.profile.general.studentCodeInline', {
                      code: person.studentCode,
                    })}
                  </Typography>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.5, sm: 2 }}
                  sx={{ mb: 2, color: 'text.secondary' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <EmailOutlined fontSize="small" />
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {headerEmail(person.email) ?? t('sis:academicRecord.noEmail')}
                    </Typography>
                  </Stack>
                  {person.phone && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PhoneOutlined fontSize="small" />
                      <Typography variant="body2">{person.phone}</Typography>
                    </Stack>
                  )}
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                  {/* ── Lifecycle types — outlined */}
                  {/* «Egresado» UNA vez: con la insignia de abajo, no también como chip. */}
                  {person.types
                    .filter((t) => LIFECYCLE_TYPES.includes(t) && !(isAlumni && t === 'alumni'))
                    .map((type) => (
                      <Chip
                        key={type}
                        label={t(`people.types.${type}`, { defaultValue: type })}
                        size="small"
                        color={TYPE_COLORS[type] ?? 'default'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  {/* ── Institutional roles (institutionalRoles field first, fallback to types[]) — filled green */}
                  {(person.institutionalRoles.length
                    ? person.institutionalRoles
                    : person.types.filter((t) => INSTITUTIONAL_TYPES.includes(t))
                  ).map((role) => (
                    <Chip
                      key={`inst-${role}`}
                      label={t(`people.types.${role}`, { defaultValue: role })}
                      size="small"
                      color="success"
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  ))}
                  {/* ── System roles (TenantRoles in types[]) — outlined with role color */}
                  {person.types
                    .filter((t) => SYSTEM_ROLES.includes(t))
                    .map((type) => (
                      <Chip
                        key={`sys-${type}`}
                        label={t(`people.types.${type}`, { defaultValue: type })}
                        size="small"
                        color={TYPE_COLORS[type] ?? 'default'}
                        variant="outlined"
                        sx={{ fontWeight: 600 }}
                      />
                    ))}
                  {/* ── Tags — la marca de fallecido no va como chip crudo: el aviso
                      de abajo ya lo dice en palabras (para todos desde M4·D1). */}
                  {person.tags
                    .filter((tag) => tag !== DECEASED_IN_SOURCE_TAG)
                    .map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        variant="filled"
                        sx={{ bgcolor: 'action.selected' }}
                      />
                    ))}
                </Stack>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                {/* ── «Ver portal»: el portal del alumno, en solo lectura ──
                    Abre una vista que el servidor audita (quién, a quién,
                    cuándo, con qué rol) y valida en cada lectura. Solo para
                    alumnos que no figuran como fallecidos en el origen. */}
                {canViewPortal && canOpenPortalView(person) && (
                  <Tooltip title={t('people.actions.viewPortalTooltip')}>
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityOutlined />}
                        disabled={openingPortal || !currentTenantId}
                        onClick={async () => {
                          setPortalError(null);
                          setOpeningPortal(true);
                          try {
                            const view = await portalViewApi.open({
                              tenantId: currentTenantId!,
                              personId: person.id,
                            });
                            queryClient.removeQueries();
                            usePortalViewStore.getState().start(view);
                            navigate('/portal-v2');
                          } catch (e) {
                            setPortalError(errorText(e, t('sis:people.actions.viewPortalError')));
                          } finally {
                            setOpeningPortal(false);
                          }
                        }}
                        sx={{
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                          },
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('people.actions.viewPortal')}
                      </Button>
                    </span>
                  </Tooltip>
                )}

                {/* ── Insignia de egresado (sus acciones van en el menú: «Ver portal», «Dar acceso») ── */}
                {isAlumni && (
                  <Chip
                    icon={<WorkspacePremiumOutlined />}
                    label={t('sis:people.types.alumni')}
                    color="info"
                    variant="filled"
                    sx={{ fontWeight: 700 }}
                  />
                )}

                {/* ── Acciones dropdown ── */}
                <Tooltip title={t('people.actions.more')}>
                  <IconButton
                    size="small"
                    onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <MoreVertOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={actionsMenuAnchor}
                  open={actionsMenuOpen}
                  onClose={() => setActionsMenuAnchor(null)}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  slotProps={{ paper: { sx: { minWidth: 220, borderRadius: 2, mt: 0.5 } } }}
                >
                  {/* Editar */}
                  <MenuItem
                    onClick={() => {
                      setActionsMenuAnchor(null);
                      setIsEditing(true);
                    }}
                  >
                    <ListItemIcon>
                      <EditOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('people.profile.edit')}</ListItemText>
                  </MenuItem>

                  {/* Alumno o egresado (o quien ya tiene cuenta); nunca un fallecido ni
                      un correo provisorio: lo mismo que exige el servidor. */}
                  {canGiveAccess(person) && (
                    <MenuItem
                      onClick={() => {
                        setActionsMenuAnchor(null);
                        handleResendWelcomeEmail();
                      }}
                      disabled={resending}
                    >
                      <ListItemIcon>
                        {resending ? (
                          <CircularProgress size={16} />
                        ) : (
                          <MarkEmailUnread fontSize="small" color="info" />
                        )}
                      </ListItemIcon>
                      <ListItemText>
                        {resending
                          ? t('people.actions.sending')
                          : person.authUid
                            ? t('people.actions.resendAccess')
                            : t('people.actions.grantAccess')}
                      </ListItemText>
                    </MenuItem>
                  )}

                  {/* Actualizar correo — solo si ya tiene cuenta */}
                  {!!person.authUid && isAdmin && !deceased && (
                    <MenuItem
                      onClick={() => {
                        setActionsMenuAnchor(null);
                        setUpdateEmailOpen(true);
                        setNewEmailInput('');
                        setUpdateEmailError(null);
                      }}
                    >
                      <ListItemIcon>
                        <ManageAccountsOutlined fontSize="small" color="warning" />
                      </ListItemIcon>
                      <ListItemText>{t('people.actions.updateAccessEmail')}</ListItemText>
                    </MenuItem>
                  )}

                  {/* Divider + Eliminar */}
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      setActionsMenuAnchor(null);
                      setNuclearOpen(true);
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon>
                      <DeleteForeverOutlined fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>{t('people.actions.deletePermanently')}</ListItemText>
                  </MenuItem>
                </Menu>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </Paper>

      {portalError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPortalError(null)}>
          {portalError}
        </Alert>
      )}

      {deceased && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          <strong>{t('academicRecord.deceased.notice')}</strong> {t('academicRecord.deceased.help')}
        </Alert>
      )}

      {/* 360 View Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v: PersonDetailTabKey) => setTabValue(v)}
          aria-label="360 profile tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: { xs: '0.85rem', sm: '1rem' },
              minHeight: { xs: 48, sm: 60 },
              minWidth: { xs: 'auto', sm: 90 },
              px: { xs: 1.5, sm: 2 },
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} value={tab.key} label={t(tab.label)} id={`person-tab-${tab.key}`} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
        <CustomTabPanel value={activeTab} index="general">
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="600">
                    {t('people.profile.general.personalData')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {person.studentCode && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('sis:people.profile.general.studentCode')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {person.studentCode}
                        </Typography>
                      </Box>
                    )}
                    {registration.kind === 'imported' ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('sis:people.profile.general.importedOn')}
                        </Typography>
                        <Typography variant="body2">
                          {formatDate(registration.at, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('people.profile.general.registeredAt')}
                        </Typography>
                        <Typography variant="body2">
                          {registration.at
                            ? formatDate(registration.at, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })
                            : '—'}
                        </Typography>
                      </Box>
                    )}
                    {registration.kind === 'imported' && firstTermQ.data && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('sis:people.profile.general.firstTerm')}
                        </Typography>
                        <Typography variant="body2">{firstTermQ.data}</Typography>
                      </Box>
                    )}
                    {person.organizationName && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('sis:people.profile.general.organization')}
                        </Typography>
                        <Typography variant="body2" fontWeight="500">
                          {person.organizationName}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('sis:people.profile.general.email')}
                      </Typography>
                      {/* La MISMA regla que el encabezado: un correo provisorio no
                          es de nadie y no se muestra crudo. */}
                      <Typography variant="body2">
                        {headerEmail(person.email) ?? t('sis:academicRecord.noEmail')}
                      </Typography>
                    </Box>
                    {person.phone && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('sis:people.profile.general.phone')}
                        </Typography>
                        <Typography variant="body2">{person.phone}</Typography>
                      </Box>
                    )}
                    {/* El id interno solo le sirve a soporte: lo ve la plataforma. */}
                    {isPlatformAdmin && (
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('people.profile.general.systemId')}
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {person.id}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="600">
                    {t('people.profile.general.additionalNotes')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontStyle: person.notes ? 'normal' : 'italic', whiteSpace: 'pre-wrap' }}
                  >
                    {person.notes || t('people.profile.general.noNotes')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {canViewPortal && hasStudentPortal(person) && currentTenantId && (
              <Grid item xs={12}>
                <PortalAccessCard tenantId={currentTenantId} personId={person.id} />
              </Grid>
            )}
          </Grid>
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index="admissions">
          <PersonAdmissionsTab personId={person.id} />
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index="enrollments">
          <PersonEnrollmentsTab personId={person.id} importedRecord={importedRecord} />
        </CustomTabPanel>

        <CustomTabPanel value={activeTab} index={credentialRecord ? 'academicRecord' : 'grades'}>
          <Box sx={{ py: 2 }}>
            {credentialRecord ? (
              progressPanel((p) => <AcademicRecordTab progress={p} />)
            ) : (
              <StudentTranscript
                studentId={person.id}
                studentName={`${person.firstName} ${person.lastName}`}
              />
            )}
          </Box>
        </CustomTabPanel>

        <CustomTabPanel
          value={activeTab}
          index={credentialRecord ? 'credentialProgress' : 'progressByProgram'}
        >
          <Box sx={{ py: 2 }}>
            {credentialRecord ? (
              progressPanel((p) => <CredentialProgressTab progress={p} />)
            ) : (
              <ProgressByProgramTab personId={person.id} />
            )}
          </Box>
        </CustomTabPanel>

        {/* H5: matrícula a programa — registrar, pausar, reactivar, egresar y
            retirar, sin depender de una admisión. */}
        <CustomTabPanel value={activeTab} index="programEnrollment">
          <Box sx={{ py: 2 }}>
            <ProgramEnrollmentTab personId={person.id} />
          </Box>
        </CustomTabPanel>

        {/* Tab Pagos — solo para alumnos activos */}
        {isActiveStudent && (
          <CustomTabPanel value={activeTab} index="payments">
            <TuitionSummaryCard studentId={person.id} isAdmin={true} />
          </CustomTabPanel>
        )}

        {/* Tab Becas — solo para alumnos activos */}
        {isActiveStudent && (
          <CustomTabPanel value={activeTab} index="scholarships">
            <StudentScholarshipsTab studentId={person.id} />
          </CustomTabPanel>
        )}

        <CustomTabPanel value={activeTab} index="crm">
          <Box sx={{ py: 2 }}>
            <CrmTimeline personId={person.id} />
          </Box>
        </CustomTabPanel>
      </Box>

      {isEditing && (
        <EditPersonDialog
          person={person}
          onClose={() => setIsEditing(false)}
          tenantId={currentTenantId}
        />
      )}

      {/* ── Update Login Email Dialog ─────────────────────────────────────────── */}
      <Dialog
        open={updateEmailOpen}
        onClose={() => {
          if (!updatingEmail) {
            setUpdateEmailOpen(false);
            setUpdateEmailError(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ManageAccountsOutlined /> Actualizar Correo de Acceso
        </DialogTitle>
        <DialogContent>
          {updateEmailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {updateEmailError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>
            Se actualizará el correo de inicio de sesión de{' '}
            <strong>
              {person.firstName} {person.lastName}
            </strong>
            .
            <br />
            <br />
            Correo actual: <strong>{person.email}</strong>
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Nuevo correo electrónico"
            type="email"
            value={newEmailInput}
            onChange={(e) => setNewEmailInput(e.target.value)}
            disabled={updatingEmail}
            autoComplete="off"
            placeholder="nuevo@correo.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleUpdateEmail();
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setUpdateEmailOpen(false);
              setUpdateEmailError(null);
            }}
            disabled={updatingEmail}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateEmail}
            disabled={updatingEmail || !newEmailInput.trim()}
            sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
          >
            {updatingEmail ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1 }} />
                Actualizando...
              </>
            ) : (
              '✅ Actualizar correo'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Resend Email Snackbar ──────────────────────────────────────────── */}
      <Snackbar
        open={resendSnack.open}
        autoHideDuration={5000}
        onClose={() => setResendSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={resendSnack.severity}
          onClose={() => setResendSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {resendSnack.msg}
        </Alert>
      </Snackbar>

      {/* ── Nuclear Delete Confirm Dialog ─────────────────────────────────────── */}
      <Dialog
        open={nuclearOpen}
        onClose={() => {
          if (!nuclearDeleting) setNuclearOpen(false);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DeleteForeverOutlined /> Eliminar Permanentemente
        </DialogTitle>
        <DialogContent>
          {nuclearError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {nuclearError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 3 }}>
            Esta es una <strong>acción destructiva</strong>. Se eliminará el registro de la persona,
            su contacto CRM, actividades, admisiones y matrículas asociadas.
            <br />
            <br />
            Para confirmar, escribe el nombre completo del registro:
            <br />
            <Typography
              component="span"
              fontWeight="bold"
              color="error.main"
              sx={{ mt: 1, display: 'block' }}
            >
              {person.firstName} {person.lastName}
            </Typography>
          </DialogContentText>

          <TextField
            fullWidth
            size="small"
            placeholder="Escribe el nombre completo aquí"
            value={nuclearConfirmName}
            onChange={(e) => setNuclearConfirmName(e.target.value)}
            disabled={nuclearDeleting}
            autoComplete="off"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setNuclearOpen(false)} disabled={nuclearDeleting}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleNuclearDelete}
            disabled={
              nuclearDeleting ||
              nuclearConfirmName.trim().toUpperCase() !==
                `${person.firstName} ${person.lastName}`.trim().toUpperCase()
            }
          >
            {nuclearDeleting ? 'Eliminando...' : 'Eliminar permanentemente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
